import React, { useEffect, useRef, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { ArrowRight, Hourglass, CheckCircle, XCircle, Users, GraduationCap, Briefcase, Database, Buildings, BookOpen, Certificate, ArrowUp, ArrowDown, ChartBar, Clock, Broadcast, EnvelopeSimple, WhatsappLogo, ChatCircleText, Warning } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const HEALTH_POLL_MS = 30_000;

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [due, setDue] = useState(null);
  const [health, setHealth] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [notifHealth, setNotifHealth] = useState(null);
  const [pulse, setPulse] = useState(false); // brief flash when a fresh poll lands
  const prevTotal = useRef(null);

  useEffect(() => {
    api.get("/registrations/stats").then((r) => setStats(r.data));
    api.get("/registrations").then((r) => setRecent(r.data.slice(0, 5)));
    api.get("/placements/due-review").then((r) => setDue(r.data)).catch(() => setDue(null));
    api.get("/registrations/analytics/overview").then((r) => setAnalytics(r.data)).catch(() => setAnalytics(null));
    api.get("/settings/notifications/health").then((r) => setNotifHealth(r.data)).catch(() => setNotifHealth(null));
  }, []);

  // Live poll for the DB health card every 30s while the dashboard is open
  useEffect(() => {
    let cancelled = false;
    const fetchHealth = async () => {
      try {
        const { data } = await api.get("/health/db");
        if (cancelled) return;
        setHealth(data);
        // Flash the dot when the total changed since the last poll
        const newTotal = data?.students?.total ?? null;
        if (prevTotal.current !== null && newTotal !== prevTotal.current) {
          setPulse(true);
          setTimeout(() => setPulse(false), 1200);
        }
        prevTotal.current = newTotal;
      } catch {
        // Silent fail — keep the previous snapshot visible
      }
    };
    fetchHealth();
    const id = setInterval(fetchHealth, HEALTH_POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") fetchHealth(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  const cards = [
    { key: "pending", label: "Pending", value: stats?.pending ?? "—", icon: Hourglass, color: "var(--sms-warning)", testid: "stat-pending" },
    { key: "approved", label: "Approved", value: stats?.approved ?? "—", icon: CheckCircle, color: "var(--sms-primary)", testid: "stat-approved" },
    { key: "enrolled", label: "Enrolled", value: stats?.enrolled ?? "—", icon: GraduationCap, color: "var(--sms-success)", testid: "stat-enrolled" },
    { key: "rejected", label: "Rejected", value: stats?.rejected ?? "—", icon: XCircle, color: "var(--sms-danger)", testid: "stat-rejected" },
    { key: "total", label: "Total", value: stats?.total ?? "—", icon: Users, color: "#0F172A", testid: "stat-total" },
  ];

  return (
    <AdminLayout>
      <div className="mb-10">
        <div className="font-mono-sms text-xs uppercase tracking-[0.3em] text-[var(--sms-primary)] mb-3">Overview</div>
        <h1 className="font-display text-5xl font-black tracking-tighter">
          Registrar's control room.
        </h1>
        <p className="text-slate-500 mt-2">A live snapshot of admissions for Phase 01.</p>
      </div>

      {due && due.total > 0 && (
        <Link
          to="/admin/placements"
          data-testid="placements-due-banner"
          className="block border border-amber-300 bg-amber-50 px-5 py-4 mb-8 hover:border-amber-500 transition-colors"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Briefcase size={20} weight="duotone" className="text-amber-600" />
              <div>
                <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-amber-700">Placements need attention</div>
                <div className="text-sm text-slate-900 font-medium">
                  {due.under_review > 0 && <>{due.under_review} awaiting review · </>}
                  {due.overdue > 0 && <>{due.overdue} past their end date</>}
                </div>
              </div>
            </div>
            <ArrowRight size={16} weight="bold" className="text-amber-700" />
          </div>
        </Link>
      )}

      {/* Stat grid */}
      <div className="grid grid-cols-1 md:grid-cols-5 border border-slate-200 mb-12" data-testid="stats-grid">
        {cards.map((c, i) => {
          const Icon = c.icon;
          return (
            <div
              key={c.key}
              data-testid={c.testid}
              className={`p-8 ${i < cards.length - 1 ? "md:border-r border-slate-200" : ""} ${i < cards.length - 1 ? "border-b md:border-b-0" : ""}`}
            >
              <div className="flex items-start justify-between mb-6">
                <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  {c.label}
                </div>
                <Icon size={20} weight="duotone" style={{ color: c.color }} />
              </div>
              <div className="font-display text-5xl font-black tracking-tighter">
                {c.value}
              </div>
            </div>
          );
        })}
      </div>

      {/* Database health */}
      {health && (
        <div className="border border-slate-200 mb-12" data-testid="db-health-card">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Database size={16} weight="duotone" className="text-[var(--sms-primary)]" />
              <div className="font-mono-sms text-xs uppercase tracking-[0.2em]">Database health</div>
              <span
                data-testid="health-live-dot"
                className={`ml-2 inline-block w-2 h-2 rounded-full transition-all ${
                  pulse
                    ? "bg-[var(--sms-success)] scale-150 shadow-[0_0_0_4px_rgba(22,163,74,0.25)]"
                    : "bg-[var(--sms-success)]"
                }`}
                title="Auto-refreshes every 30 s"
              />
              <span className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-400">
                Live · 30s
              </span>
            </div>
            {health.last_activity && (
              <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Last activity · {new Date(health.last_activity).toLocaleString()}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5">
            <HealthCell
              icon={Users} label="Students" value={health.students.total}
              sub={`${health.students.enrolled} enrolled · ${health.students.pending} pending`}
              delta={health.students.submitted_1h} deltaLabel="new this hour"
              testid="health-students"
            />
            <HealthCell
              icon={BookOpen} label="Courses" value={health.courses.active}
              sub={health.courses.archived ? `${health.courses.archived} archived` : "active"}
              testid="health-courses"
            />
            <HealthCell
              icon={Buildings} label="Companies" value={health.companies.active}
              sub="placement partners"
              testid="health-companies"
            />
            <HealthCell
              icon={Briefcase} label="Placements"
              value={health.placements.active + health.placements.under_review + health.placements.approved}
              sub={`${health.placements.under_review} review · ${health.placements.approved} approved`}
              delta={health.placements.added_1h} deltaLabel="added this hour"
              testid="health-placements"
            />
            <HealthCell
              icon={Certificate} label="Certificates" value={health.placements.certificates_issued}
              sub="issued"
              delta={health.placements.certificates_1h} deltaLabel="issued this hour"
              testid="health-certificates" border={false}
            />
          </div>
        </div>
      )}

      {/* Analytics */}
      {analytics && (
        <div className="grid md:grid-cols-3 gap-6 mb-8" data-testid="analytics-row">
          <div className="border border-slate-200 p-6 md:col-span-1" data-testid="analytics-time-to-decision">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={14} weight="duotone" className="text-[var(--sms-primary)]" />
              <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">Time to decision</div>
            </div>
            <div className="font-display text-4xl font-black tracking-tighter" data-testid="ttd-avg-hours">
              {analytics.time_to_decision.avg_hours} <span className="text-base font-mono-sms tracking-tight text-slate-500">hrs</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Avg across {analytics.time_to_decision.sample_size} decisions
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-3 gap-2 font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-500">
              <div>
                <div className="text-slate-400">Median</div>
                <div className="text-slate-900 mt-0.5" data-testid="ttd-median-hours">{analytics.time_to_decision.median_hours}h</div>
              </div>
              <div>
                <div className="text-slate-400">Fastest</div>
                <div className="text-slate-900 mt-0.5">{analytics.time_to_decision.min_hours}h</div>
              </div>
              <div>
                <div className="text-slate-400">Slowest</div>
                <div className="text-slate-900 mt-0.5">{analytics.time_to_decision.max_hours}h</div>
              </div>
            </div>
          </div>
          <div className="border border-slate-200 p-6 md:col-span-2" data-testid="analytics-course-mix">
            <div className="flex items-center gap-2 mb-1">
              <ChartBar size={14} weight="duotone" className="text-[var(--sms-primary)]" />
              <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">Course mix · enrolled</div>
            </div>
            <div className="text-xs text-slate-500 mb-3">
              {analytics.totals.enrolled} enrolled across {analytics.course_mix.length} course{analytics.course_mix.length === 1 ? "" : "s"}
            </div>
            {analytics.course_mix.length === 0 ? (
              <div className="text-sm text-slate-400 py-8 text-center">No enrolments yet.</div>
            ) : (
              <div style={{ width: "100%", height: 200 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={analytics.course_mix.slice(0, 8).map((c) => ({ name: c.course_code || c.course_name, full: c.course_name, count: c.count }))}
                    margin={{ top: 5, right: 10, bottom: 5, left: 0 }}
                  >
                    <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} fontFamily="JetBrains Mono, monospace" />
                    <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ borderRadius: 0, border: "1px solid #0f172a", fontFamily: "Inter, sans-serif", fontSize: 12 }}
                      labelFormatter={(_, payload) => (payload?.[0]?.payload?.full) || ""}
                    />
                    <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                      {analytics.course_mix.slice(0, 8).map((_, i) => (
                        <Cell key={i} fill="var(--sms-primary)" fillOpacity={1 - (i * 0.08)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notifications health */}
      {notifHealth && (
        <div className="border border-slate-200 mb-8" data-testid="notif-health-card">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center gap-2">
              <Broadcast size={14} weight="duotone" className="text-[var(--sms-primary)]" />
              <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">Notifications health · last 7 days</div>
            </div>
            <Link to="/admin/settings?tab=notifications" data-testid="notif-health-settings-link" className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-[var(--sms-primary)] inline-flex items-center gap-1">
              Configure <ArrowRight size={11} weight="bold" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-200">
            {[
              { id: "email", label: "Email", icon: <EnvelopeSimple size={16} weight="duotone" /> },
              { id: "whatsapp", label: "WhatsApp", icon: <WhatsappLogo size={16} weight="duotone" /> },
              { id: "sms", label: "SMS", icon: <ChatCircleText size={16} weight="duotone" /> },
            ].map((ch) => (
              <ChannelHealthRow
                key={ch.id}
                icon={ch.icon}
                label={ch.label}
                data={notifHealth.channels[ch.id]}
                buckets={notifHealth.buckets}
                testid={`notif-channel-${ch.id}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Recent applications */}
      <div className="border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="font-mono-sms text-xs uppercase tracking-[0.2em]">Recent Applications</div>
          <Link to="/admin/pending" data-testid="dashboard-view-all" className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-primary)] hover:underline inline-flex items-center gap-1">
            View all <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">
              <th className="text-left px-6 py-3 font-medium">Name</th>
              <th className="text-left px-6 py-3 font-medium">Course</th>
              <th className="text-left px-6 py-3 font-medium">Submitted</th>
              <th className="text-left px-6 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {recent.length === 0 && (
              <tr>
                <td colSpan={4} className="px-6 py-12 text-center text-slate-400 text-sm">
                  No applications yet.
                </td>
              </tr>
            )}
            {recent.map((r) => (
              <tr key={r.id} className="border-t border-slate-200 hover:bg-slate-50">
                <td className="px-6 py-4">
                  <div className="font-medium">{r.full_name}</div>
                  <div className="text-xs text-slate-500">{r.email}</div>
                </td>
                <td className="px-6 py-4 text-slate-700">{r.course_name || "—"}</td>
                <td className="px-6 py-4 font-mono-sms text-xs text-slate-500">
                  {new Date(r.submitted_at).toLocaleString()}
                </td>
                <td className="px-6 py-4">
                  <StatusBadge status={r.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}

function StatusBadge({ status }) {
  const cls = status === "approved" ? "sms-badge-approved" : status === "rejected" ? "sms-badge-rejected" : "sms-badge-pending";
  return <span className={`sms-badge ${cls}`}>{status}</span>;
}

function ChannelHealthRow({ icon, label, data, buckets, testid }) {
  if (!data) return null;
  const max = Math.max(1, ...(data.spark || [0]));
  const total = data.stats?.total_7d || 0;
  const live = !!data.live;
  return (
    <div className="p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="inline-flex items-center gap-2 text-slate-700">
          {icon}
          <span className="font-display text-sm font-bold tracking-tight">{label}</span>
        </div>
        <span
          data-testid={`${testid}-status`}
          className={`font-mono-sms text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 border ${
            live
              ? "border-[var(--sms-success)] text-[var(--sms-success)] bg-emerald-50"
              : "border-[var(--sms-warning)] text-[#92400E] bg-amber-50"
          }`}
        >
          {live ? "Live" : "Mocked"}
        </span>
      </div>
      {data.from && (
        <div className="font-mono-sms text-[10px] text-slate-500 truncate mb-3" title={data.from} data-testid={`${testid}-from`}>
          {data.from}
        </div>
      )}
      {/* Sparkline */}
      <div className="flex items-end gap-1 h-10 mb-2" data-testid={`${testid}-sparkline`}>
        {(data.spark || []).map((v, i) => {
          const h = Math.max(2, Math.round((v / max) * 36));
          const isToday = i === (data.spark || []).length - 1;
          return (
            <div
              key={i}
              title={`${buckets?.[i] || ""}: ${v} send${v === 1 ? "" : "s"}`}
              style={{ height: `${h}px`, width: "12px" }}
              className={`${
                v === 0
                  ? "bg-slate-100"
                  : live
                  ? "bg-[var(--sms-success)]/70"
                  : "bg-[var(--sms-warning)]/70"
              } ${isToday ? "outline outline-1 outline-slate-900" : ""}`}
            />
          );
        })}
      </div>
      <div className="grid grid-cols-3 gap-2 font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-500">
        <div>
          <div className="text-slate-400">Total</div>
          <div className="text-slate-900 mt-0.5" data-testid={`${testid}-total`}>{total}</div>
        </div>
        <div>
          <div className="text-slate-400">{live ? "Sent" : "Mocked"}</div>
          <div className="text-slate-900 mt-0.5" data-testid={`${testid}-sent`}>
            {live ? data.stats?.sent_7d : data.stats?.mocked_7d}
          </div>
        </div>
        <div>
          <div className="text-slate-400">Failed</div>
          <div className={`mt-0.5 ${data.stats?.failed_7d ? "text-[var(--sms-error)]" : "text-slate-900"}`} data-testid={`${testid}-failed`}>
            {data.stats?.failed_7d || 0}
          </div>
        </div>
      </div>
    </div>
  );
}

function HealthCell({ icon: Icon, label, value, sub, delta, deltaLabel, testid, border = true }) {
  const showDelta = typeof delta === "number" && delta > 0;
  return (
    <div data-testid={testid} className={`p-5 ${border ? "md:border-r border-slate-200" : ""} border-b md:border-b-0`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} weight="duotone" className="text-slate-500" />
        <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      </div>
      <div className="flex items-baseline gap-2">
        <div className="font-display text-3xl font-black tracking-tighter">{value}</div>
        {showDelta && (
          <span
            data-testid={`${testid}-delta`}
            className="inline-flex items-center gap-0.5 font-mono-sms text-[10px] uppercase tracking-[0.15em] text-[var(--sms-success)]"
          >
            <ArrowUp size={10} weight="bold" />
            {delta}
          </span>
        )}
      </div>
      {sub && <div className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-400 mt-1 truncate">{sub}</div>}
      {showDelta && deltaLabel && (
        <div className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-[var(--sms-success)] mt-0.5 truncate">
          {deltaLabel}
        </div>
      )}
    </div>
  );
}
